import { Subscription } from 'rxjs';

import { Component, Input, ElementRef, ViewChild, ChangeDetectorRef, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { FeatureUpgradeService } from './upgrade.service';
import { Session } from '@services/session';
import { StatsigService } from '@app/services/statsig.service';
import { FleetCardsFeatureFlagService } from '@app/services/feature-flags/fleet-cards';
import { TimeZoneFormatService } from '@app/admin/fleet-card/shared/billing-tz.service';
import { PendoAnalytics } from '@services/pendo-analytics';
import { MaintenanceFeatureFlagService } from '@app/services/feature-flags/maintenance';
import { FleetGrowthFeatureFlagService } from '@services/feature-flags/growth';

import { pushUpsellViewGTMEvent, UPSELL_COMPONENT_EVENT_NAME } from '@lib/gtm-events';
import { setCookie, getCookie } from '@lib/cookies';
import { CookieKey } from '@lib/cookie-keys.const';
import { moment } from '@lib/moment';
import { mapValueToBoolean } from '@lib/utils';
import { trackJs } from '@lib/trackjs';

import { ICardApplication } from '@app/admin/fleet-card/card-application/card-application.interface';

import { UPSELL_FEATURE_KEYS, REBATE_OFFERS } from '@app/dashboard/fleet-cards/main.constants';
import { FLEET_CARD_ADMIN_ROUTES } from '@app/admin/fleet-card/shared/utils';
import { USER_DATE_FORMAT, USER_TIME_FORMAT } from '@app/lib/moment';

import {
  IUpgradeOptions,
  UPSELL_CONFIG,
  UPSELL_MODE,
  isValidUpsellFeatureKey,
  upsellEventId,
  FEATURE_FLAG_UPSELL_CONFIG_KEYS,
  CAMPAIGN_MAP,
  IGeneralUpsellParams,
  UPSELL_CAMPAIGN_NAMES,
} from '@misc/upsell/upsell-config';

const ERROR_HIDE_TIME = 30000;

const UPSELL_KEY = {
  fuel: 'Fuel Hub',
  safety: 'Safety Hub',
  maintenance: 'Maintenance',
  fleet_card_upsell_admin: 'Fleet Cards Admin',
  fleet_card_upsell_user: 'Fleet Cards Manager',
};

const PRODUCT_INTEREST_CAMPAIGN_MAP = {
  'Smart Dashcam': 'get_quote_dashcam',
  'Trailer Tracking': 'get_quote_ag',
  'ELD Compliance': 'get_quote_vg',
  'Fleet Cards': 'fleet_cards',
};

const MAINTENANCE_CAMPAIGN = 'maintenance_pro';
const UPSELL_ID = {
  adminApplicationStarted: 'fleet-card-upsell-admin-application-started',
};

interface ITrackRemove5CentsOffExperimentData {
  rebate_offer: string;
  url: string;
}

@Component({
  selector: 'feature-upgrade',
  templateUrl: './feature-upgrade.component.html',
  styleUrls: ['./feature-upgrade.component.scss', '../../styles/upgrade.scss'],
})
export class FeatureUpgradeComponent implements OnInit {
  @ViewChild('trialActivatedModal', { static: true }) public trialActivatedModal;
  @ViewChild('thankYouModal', { static: true }) public thankYouModal;
  @Input() public featureKey: string;
  @Input() public upsellKey: string;
  @Input() public getQuoteproductInterest: string;
  @Input() public cardDraftApplicationData: ICardApplication;
  public options: IUpgradeOptions;
  public accountManager: any;
  public isAMAssigned: boolean;
  public showError: boolean = false;
  public readonly defaultEmail = 'sales@gomotive.com';
  public showPlayBtn: boolean = true;
  public featureServiceSubscription: Subscription;
  public companyId: number;
  public isFleetCardUpsellEnabled: boolean;
  public phoneNumberTel: string = '+1 855-434-3564';
  public isGetDemoBtnDisabled: boolean = false;
  public isCampaignBtnDisabled: boolean = false;
  public upsellComponentId: string;
  public isCardsSavingsFinderP1Enabled: boolean;
  public isEOQUpsellQ12023Enabled: boolean;
  private el: HTMLElement;
  private isFeatureTrialEnded: boolean;
  private isFleetManager: boolean;
  private isFleetAdmin: boolean;
  private isCardsRemove5CentsOffExperimentEnabled: boolean;
  private remove5CentsOffExperimentValue: string;

  constructor(
    elRef: ElementRef,
    private featureUpgradeService: FeatureUpgradeService,
    private session: Session,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private statsigService: StatsigService,
    private fleetCardsFeatureFlagService: FleetCardsFeatureFlagService,
    private timeZoneFormat: TimeZoneFormatService,
    private pendo: PendoAnalytics,
    private maintenanceFeatureFlagService: MaintenanceFeatureFlagService,
    private fgFeatureFlagService: FleetGrowthFeatureFlagService,
  ) {
    this.el = elRef.nativeElement;
    this.isFeatureTrialEnded = session.isTrialEnded();
    this.accountManager = session.getAccountManager();
    this.isAMAssigned = !!this.accountManager;
    this.isFleetManager = session.isDispatcher();
    this.isFleetAdmin = session.isAdmin();
    this.isCardsSavingsFinderP1Enabled = this.fleetCardsFeatureFlagService.isCardsSavingsFinderP1Enabled();
    this.isCardsRemove5CentsOffExperimentEnabled = this.fleetCardsFeatureFlagService.isCardsRemove5CentsOffExperimentEnabled();
    this.remove5CentsOffExperimentValue = this.fleetCardsFeatureFlagService.getFleetCardsRemoving5CentsOffExperimentValue();
    this.isEOQUpsellQ12023Enabled = fgFeatureFlagService.isEOQUpsellQ12023Enabled();
  }

  public ngOnInit(): void {
    this.options = UPSELL_CONFIG[this.featureKey];
    this.setFleetCardUpsellState();
    this.setGetDemoState();
    this.setCardsUpsellPopupState();
    this.upsellComponentId = isValidUpsellFeatureKey(this.featureKey) ? upsellEventId(this.el, this.featureKey) : '';
  }

  public ngAfterViewInit(): void {
    // element is visible
    if (this.el.offsetParent !== null) {
      pushUpsellViewGTMEvent(UPSELL_COMPONENT_EVENT_NAME['feature-upgrade'], this.upsellComponentId);
    }
  }

  public get user() {
    return {
      id: this.session.getUserId(),
      firstName: this.session.getUserFirstName(),
      email: this.session.getUserEmail(),
      lastName: this.session.getUserLastName(),
      companyName: this.session.getCompany().name,
    };
  }

  public get isFeatureKeyAdminOrStarted(): boolean {
    return this.featureKey === UPSELL_FEATURE_KEYS.ADMIN || this.featureKey === UPSELL_FEATURE_KEYS.ADMIN_APPLICATION_STARTED;
  }

  public updateUpsellPageDescription(): void {
    if (this.remove5CentsOffExperimentValue === REBATE_OFFERS['5 Cents Off']) {
      this.options.description = this.options.fiveCentsOffDescription;
    } else if (this.remove5CentsOffExperimentValue === REBATE_OFFERS['3 Cents Off']) {
      this.options.description = this.options.threeCentsOffDescription;
    }
  }

  public setFleetCardUpsellState(): void {
    if (
      this.featureKey === UPSELL_FEATURE_KEYS.ADMIN ||
      this.featureKey === UPSELL_FEATURE_KEYS.USER ||
      this.featureKey === UPSELL_FEATURE_KEYS.ADMIN_APPLICATION_STARTED
    ) {
      const trackData: ITrackRemove5CentsOffExperimentData = {
        rebate_offer: this.remove5CentsOffExperimentValue,
        url: this.router.url,
      };
      if (this.isCardsRemove5CentsOffExperimentEnabled) {
        this.pendo.trackEvent('Remove 5 Cents Off Experiment', trackData);
      }

      this.isFleetCardUpsellEnabled = true;
      this.isAMAssigned = false;
      if (this.isCardsRemove5CentsOffExperimentEnabled && this.featureKey !== UPSELL_FEATURE_KEYS.ADMIN_APPLICATION_STARTED) {
        this.updateUpsellPageDescription();
      }
      if (this.featureKey === UPSELL_FEATURE_KEYS.USER) this.updateDescriptionOnRequestReceived();
      else if (this.featureKey === UPSELL_FEATURE_KEYS.ADMIN_APPLICATION_STARTED) this.updateAdminDescriptionOnRequestReceived();
    }
  }

  public tryForFree(): void {
    const upsellKey: string = UPSELL_KEY.hasOwnProperty(this.featureKey)
      ? this.getUpsellKey(UPSELL_MODE.trial, this.featureKey)
      : '';

    this.featureUpgradeService.getTrialFeature(this.session.getCompany().id, upsellKey).subscribe(
      () => {
        this.trialActivatedModal.show();
        setCookie(CookieKey.UPGRADE_TOPBAR_STATUS, true);
      },
      () => {
        this.handleError();
      },
    );
  }

  public redirectIfApplied(): void {
    if (this.fleetCardsUpsellRequested) {
      if (this.isEOQUpsellQ12023Enabled) {
        this.featureUpgradeService.emitRequestReceivedEvent(true);
      }
      this.router.navigateByUrl(FLEET_CARD_ADMIN_ROUTES.CARD_APPLICATION);
    } else {
      // Make the api call
      this.upgradeToPro('fleet_cards', 'Fleet Cards');
    }
  }

  // add any campaign specific function for learn more CTA in future
  public learnMore(): void {
    if (this.isCardsUpsellPopup) {
      this.entryPopupCampaignRequest('cards_upsell_pop_up', this.getQuoteproductInterest, this.upsellKey, 'Learn more');
      if (this.isEOQUpsellQ12023Enabled) {
        window.open(this.options.learnMoreUrl, '_blank');
        this.featureUpgradeService.emitRequestReceivedEvent(true);
      }
    } else {
      window.open(this.options.learnMoreUrl, '_blank');
    }
  }

  public learnMoreFleetCard(): void {
    this.router.navigate([this.options.learnMoreFcUrl]);
  }

  public entryPopupCampaignRequest(campaign: string, productInterest: string, upsellKey: string, ctaContent): void {
    this.isCampaignBtnDisabled = true;
    campaign = CAMPAIGN_MAP[this.featureKey];
    let upsellCampaignName: string = campaign;

    if (ctaContent === 'Apply now') {
      upsellCampaignName = UPSELL_CAMPAIGN_NAMES.cards_upsell_pop_up_apply_now;
    } else if (ctaContent === 'I\'m interested') {
      upsellCampaignName = UPSELL_CAMPAIGN_NAMES.cards_upsell_pop_up_interested;
    }

    this.featureUpgradeService
      .genericUpsell(upsellCampaignName)
      .subscribe(
        () => {
          this.setSuccessDescription();
          this.featureUpgradeService.emitCTAContentContextEvent(ctaContent);

          this.session.refresh();
          this.session.sessionUpdate$.subscribe((): void => {
            if (this.isCardsUpsellPopup) {
              this.cardsUpsellPopupSuccessFlow(ctaContent);
            } else if (this.isEOY2022UpsellPopup) {
              this.eoy2022UpsellPopupSuccessFlow();
            } else {
              this.featureUpgradeService.emitRequestReceivedEvent(true);
              this.thankYouModal.show();
            }

            this.cdr.detectChanges();
          });
        },
        (errorResponse: HttpErrorResponse) => {
          this.handleError();
          this.isCampaignBtnDisabled = false;
        },
      );
  }

  public getDemo(campaign: string, productInterest: string, upsellKey: string, ctaContent: string = ''): void {
    this.isGetDemoBtnDisabled = true;
    // override campaign depending upon upsell config feature key
    campaign = CAMPAIGN_MAP[this.featureKey];
    const generalUpsellParams: IGeneralUpsellParams = {
      subscribeTo: campaign,
      userEmail: this.user.email,
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      companyName: this.user.companyName,
      productInterest,
      upsellKey,
      ctaContent,
    };
    this.featureUpgradeService
      .generalUpgrade(generalUpsellParams)
      .subscribe(
        () => {
          this.session.refresh();
          this.setSuccessDescription();
          this.featureUpgradeService.emitRequestReceivedEvent(true);
        },
        (errorResponse: HttpErrorResponse) => {
          this.handleError();
          this.isGetDemoBtnDisabled = false;
        },
      );
  }

  // TODO: make sure all campaignType(s) are handled
  public upgradeToPro(campaignType: string, productInterest: string) {
    const upsellKey: string = UPSELL_KEY.hasOwnProperty(this.featureKey)
      ? this.getUpsellKey(UPSELL_MODE.upgrade, this.featureKey)
      : '';

    // TODO: maintain single source truth for campaign type
    // update for get_quote, fleet_cards etc campaign types
    if (campaignType !== 'upgrade_now') {
      campaignType = PRODUCT_INTEREST_CAMPAIGN_MAP[productInterest];
    }

    this.featureUpgradeService
      .genericUpsell(UPSELL_CAMPAIGN_NAMES[campaignType])
      .subscribe({
        next: () => {
          if (campaignType === 'fleet_cards') {
            // Handle success for fleet cards upsell
            this.fleetCardUpsellSuccessState();
          } else {
            this.thankYouModal.show();
            if (campaignType === 'upgrade_now' || campaignType === MAINTENANCE_CAMPAIGN) {
              setCookie(CookieKey.UPSELL_UPGRADE_BTN_SHOW, false, moment().add(24, 'hours'));
              this.session.refresh();
            }
          }
        },
        error: (errorResponse: HttpErrorResponse) => {
          if (campaignType === 'fleet_cards') {
            this.fleetCardUpsellFailureState(errorResponse);
          } else {
            this.handleError();
          }
        },
      });
  }

  public get showTryForFreeBtn(): boolean {
    return (
      this.options.isTrialBtn &&
      this.isFleetAdmin &&
      !this.session.isTrialRequested &&
      !this.session.isGetAQuoteRequested &&
      !this.session.isUpgradeRequested &&
      !this.session.hasProTrialEndDate
    ); // hide if trial is activated once
  }

  public get upgradeNow(): boolean {
    const showUpsellUpgradeBtn: boolean = mapValueToBoolean(getCookie(CookieKey.UPSELL_UPGRADE_BTN_SHOW));
    return (
      this.options.isUpgradeBtn &&
      (showUpsellUpgradeBtn ||
        this.session.isTrialRequested ||
        this.isFeatureTrialEnded ||
        (this.isFleetManager && !this.isFleetAdmin)) &&
      !this.session.isUpgradeRequested
    );
  }

  public get getGeneralGetTheDealRequested(): boolean {
    return this.options.isGetTheDealBtn ? !this.session.isGeneralDashcamGetTheDealRequested && this.isEOYGeneralUpsell : false;
  }

  public get getFleetViewGetTheDealRequested(): boolean {
    return this.options.isGetTheDealBtn
      ? !this.session.isFleetviewDashcamGetTheDealRequested && this.isEOYFleetViewUpsell
      : false;
  }

  public get getActivateSavingRequested(): boolean {
    return this.options.isActivateSaving ? !this.session.isActivateSavingRequested : false;
  }

  public get getDemoRequested(): boolean {
    return this.options.isGetDemoBtn ? !this.session.isGetDemoRequested : false;
  }

  public get getQuoteRequested(): boolean {
    return this.options.isGetQuoteBtn ? !this.session.isGetAQuoteRequested : false;
  }

  public get showGeneralEOYGetTheDealRequestedBtn(): boolean {
    return this.options.isGetTheDealRequestedBtn
      ? this.session.isGeneralDashcamGetTheDealRequested && this.isEOYGeneralUpsell
      : false;
  }

  public get showFleetViewEOYGetTheDealRequestedBtn(): boolean {
    return this.options.isGetTheDealRequestedBtn
      ? this.session.isFleetviewDashcamGetTheDealRequested && this.isEOYFleetViewUpsell
      : false;
  }

  public get showActivateSavingRequestedBtn(): boolean {
    return this.options.isActivateSavingRequestedBtn ? this.session.isActivateSavingRequested : false;
  }

  public get showGetDemoRequestedBtn(): boolean {
    return this.options.isGetDemoRequestedBtn ? this.session.isGetDemoRequested : false;
  }

  public get showQuoteRequestedBtn(): boolean {
    return this.options.isGetQuoteRequestedBtn ? this.session.isGetAQuoteRequested : false;
  }

  public get showUpgradeRequestedBtn(): boolean {
    return this.options.isUpgradeRequestedBtn ? this.session.isUpgradeRequested : false;
  }

  public get fleetCardsUpsellRequested(): boolean {
    return this.session.isFleetCardUpsellRequested;
  }

  public get isCardsUpsellPopup(): boolean {
    return (
      this.featureKey === FEATURE_FLAG_UPSELL_CONFIG_KEYS.CARDS_UPSELL_POPUP_ADMIN_V1 ||
      this.featureKey === FEATURE_FLAG_UPSELL_CONFIG_KEYS.CARDS_UPSELL_POPUP_ADMIN_V2 ||
      this.featureKey === FEATURE_FLAG_UPSELL_CONFIG_KEYS.CARDS_UPSELL_POPUP_NON_ADMIN ||
      this.featureKey === FEATURE_FLAG_UPSELL_CONFIG_KEYS.EOQ_Q1_2023_UPSELL
    );
  }

  public get isEOY2022UpsellPopup(): boolean {
    return this.isEOYGeneralUpsell || this.isEOYFleetViewUpsell;
  }

  public get isEOYGeneralUpsell(): boolean {
    return this.featureKey === FEATURE_FLAG_UPSELL_CONFIG_KEYS.EOY_2022_GENERAL_DASHCAM_UPSELL;
  }

  public get isEOYFleetViewUpsell(): boolean {
    return this.featureKey === FEATURE_FLAG_UPSELL_CONFIG_KEYS.EOY_2022_FLEETVIEW_DASHCAM_UPSELL;
  }

  public updateDescriptionOnRequestReceived(): void {
    if (this.isFleetCardUpsellEnabled && this.session.isFleetCardUpsellRequested) {
      this.setSuccessDescription();
    }
  }

  public updateAdminDescriptionOnRequestReceived(): void {
    if (this.options.upsellId === UPSELL_ID.adminApplicationStarted) {
      const userLocalTimeZone: string = moment.tz.guess();
      const updatedAt: string = this.cardDraftApplicationData.updated_at;
      const updatedByName: string = this.cardDraftApplicationData.updated_by.full_name;
      const updatedDate: string = this.timeZoneFormat.formatToTimeZone(userLocalTimeZone, updatedAt, USER_DATE_FORMAT);
      const updatedTime: string = this.timeZoneFormat.formatToTimeZone(userLocalTimeZone, updatedAt, USER_TIME_FORMAT);
      if (this.user.id === this.cardDraftApplicationData.updated_by.id) {
        this.options.description = $localize`Your Motive Card application was last saved on ${updatedDate}, at ${updatedTime}.`;
      } else {
        this.options.description = $localize`Your Motive Card application was last saved by ${updatedByName} on ${updatedDate}, at ${updatedTime}`;
      }
    }
  }

  private handleError(): void {
    this.showError = true;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.showError = false;
      this.cdr.detectChanges();
    }, ERROR_HIDE_TIME);
  }

  private getUpsellKey(mode: string, featureKey: string): string {
    return `${mode} ${UPSELL_KEY[featureKey]}`;
  }

  private cardsUpsellPopupSuccessFlow(ctaContent: string): void {
    this.featureUpgradeService.emitRequestReceivedEvent(true);

    if (ctaContent === 'Learn more') {
      this.router.navigate([this.options.learnMoreUrl]);
    } else if (ctaContent === 'Apply now') {
      this.router.navigate([FLEET_CARD_ADMIN_ROUTES.CARD_APPLICATION]);
    } else {
      this.thankYouModal.show();
    }
  }

  private eoy2022UpsellPopupSuccessFlow(): void {
    if (this.isEOYGeneralUpsell) {
      setTimeout(() => {
        this.featureUpgradeService.emitRequestReceivedEvent(true);
      }, 5000);
    } else if (this.isEOYFleetViewUpsell) {
      this.featureUpgradeService.emitRequestReceivedEvent(true);
    }
  }

  private fleetCardUpsellSuccessState(): void {
    this.session.refresh();
    this.session.sessionUpdate$.subscribe((): void => {
      if (this.isFeatureKeyAdminOrStarted) {
        // for admins, we can just redirect them to the card application page
        this.router.navigateByUrl(FLEET_CARD_ADMIN_ROUTES.CARD_APPLICATION);
      } else this.updateDescriptionOnRequestReceived();
    });
  }

  private fleetCardUpsellFailureState(errorResponse: HttpErrorResponse): void {
    // Handle and report error for fleet cards upsell
    if (trackJs) {
      trackJs.track(errorResponse);
    }
    if (this.isFeatureKeyAdminOrStarted) {
      // for admins, redirect them to the card application page
      this.router.navigateByUrl(FLEET_CARD_ADMIN_ROUTES.CARD_APPLICATION);
    } else {
      this.handleError();
    }
  }

  private setGetDemoState(): void {
    if (this.featureKey === 'cmrl_mm_ent_expansion' && this.showGetDemoRequestedBtn) {
      this.setSuccessDescription();
    } else if (this.featureKey === 'smb_promo_expansion' && this.showActivateSavingRequestedBtn) {
      this.setSuccessDescription();
    } else if (this.isEOYFleetViewUpsell && this.showGeneralEOYGetTheDealRequestedBtn) {
      this.setSuccessDescription();
    } else if (this.isEOYGeneralUpsell && this.showFleetViewEOYGetTheDealRequestedBtn) {
      this.setSuccessDescription();
    }
  }

  private setSuccessDescription(): void {
    if (!!this.options.successDescription) {
      this.options.description = this.options.successDescription;
    }
  }

  private setCardsUpsellPopupState(): void {
    if (this.isCardsUpsellPopup) {
      this.isAMAssigned = false;
    } else if (this.isEOY2022UpsellPopup) {
      this.isAMAssigned = false;
      this.phoneNumberTel = '+1 855-585-5772';
    }
  }
}
